import { Static, Type } from '@sinclair/typebox';
import { PREVIEW_CLIENT_EVENT_KEYS } from './taxonomy';
import { LANDING_PATH_MAX_LENGTH, REFERRER_DOMAIN_MAX_LENGTH } from './attribution-params';
import { VISITOR_ID_MAX_LENGTH } from './visitor-id';

/**
 * Attribution marketing des LEADS (acquisition Pulse + LP).
 *
 * Deux origines de leads, un seul vocabulaire (cf. `tracking/taxonomy.ts` — la
 * table TRACKING_EVENTS est la source de vérité du mapping event → régies) :
 *   - LP / widget web : le navigateur porte les identifiants pub (fbclid, _fbp/_fbc,
 *     gclid, ttclid, msclkid…, UTM) → capturés au premier message widget et
 *     persistés sur le lead.
 *   - WhatsApp (Click-to-WhatsApp) : PAS de navigateur → l'attribution repose sur le
 *     `ctwa_clid` transmis par Meta dans le webhook (`messages[].referral`) + le
 *     matching par téléphone hashé (advanced matching CAPI).
 */

/**
 * Attribution marketing d'un lead, persistée dans `lead.qualification.attribution`
 * (JSONB). Politique FIRST-TOUCH : une fois posée, une clé n'est jamais écrasée
 * (cf. merge côté runtime). C'est ce blob qui permet, des jours plus tard, de
 * renvoyer aux régies des conversions attribuables (ctwa_clid / fbc / ttclid) quand
 * le lead franchit une étape du funnel — y compris depuis une conversation WhatsApp
 * sans navigateur.
 *
 * ⚠️ Un identifiant de clic non capturé n'est JAMAIS rattrapable : les champs des
 * régies non encore actives (ttclid, msclkid…) sont capturés dès maintenant pour que
 * les premiers jours d'une future campagne restent attribuables.
 */
export const LeadAttributionSchema = Type.Object({
  /** Canal d'entrée du lead ('web_widget' | 'whatsapp' | 'messenger' | 'instagram'…). */
  channel_origin: Type.Optional(Type.String({ maxLength: 40 })),
  first_touch_at: Type.Optional(Type.String({ format: 'date-time' })),

  // UTM (LP / widget web)
  utm_source: Type.Optional(Type.String({ maxLength: 128 })),
  utm_medium: Type.Optional(Type.String({ maxLength: 128 })),
  utm_campaign: Type.Optional(Type.String({ maxLength: 128 })),
  utm_content: Type.Optional(Type.String({ maxLength: 256 })),
  utm_term: Type.Optional(Type.String({ maxLength: 256 })),
  utm_id: Type.Optional(Type.String({ maxLength: 64 })),
  /** Identifiants Meta de la pub cliquée (`hsa_cam` / `hsa_grp` / `hsa_ad`), reportés du signup. */
  meta_campaign_id: Type.Optional(Type.String({ maxLength: 32 })),
  meta_adset_id: Type.Optional(Type.String({ maxLength: 32 })),
  meta_ad_id: Type.Optional(Type.String({ maxLength: 32 })),
  referrer: Type.Optional(Type.String({ maxLength: 500 })),
  landing_page: Type.Optional(Type.String({ maxLength: 1024 })),

  // Identifiants pub navigateur — Meta / Google
  fbclid: Type.Optional(Type.String({ maxLength: 512 })),
  fbp: Type.Optional(Type.String({ maxLength: 128 })),
  fbc: Type.Optional(Type.String({ maxLength: 256 })),
  gclid: Type.Optional(Type.String({ maxLength: 512 })),
  // TikTok (ttclid = clic d'URL, ttp = cookie `_ttp` du Pixel — soumis à consentement)
  ttclid: Type.Optional(Type.String({ maxLength: 512 })),
  ttp: Type.Optional(Type.String({ maxLength: 128 })),
  // OpenAI Ads / publicités ChatGPT (oppref = clic d'URL, obref = cookie `__obref`
  // du SDK — soumis à consentement, même statut que `_fbp`/`_ttp`).
  oppref: Type.Optional(Type.String({ maxLength: 512 })),
  obref: Type.Optional(Type.String({ maxLength: 128 })),
  // Identifiant visiteur première partie (cookie `propelsia_vid`) reporté du signup :
  // `external_id` des régies qui relie le parcours avant inscription au lead.
  visitor_id: Type.Optional(Type.String({ maxLength: VISITOR_ID_MAX_LENGTH })),
  // LinkedIn / Microsoft Ads / Pinterest — capturés pour attribution future.
  li_fat_id: Type.Optional(Type.String({ maxLength: 512 })),
  msclkid: Type.Optional(Type.String({ maxLength: 512 })),
  epik: Type.Optional(Type.String({ maxLength: 512 })),

  // Click-to-WhatsApp (webhook Meta `messages[].referral`)
  ctwa_clid: Type.Optional(Type.String({ maxLength: 512 })),
  ctwa_source_url: Type.Optional(Type.String({ maxLength: 1024 })),
  ctwa_source_id: Type.Optional(Type.String({ maxLength: 128 })),
  ctwa_source_type: Type.Optional(Type.String({ maxLength: 40 })),
  ctwa_headline: Type.Optional(Type.String({ maxLength: 256 })),

  /**
   * Consentement marketing du lead (bannière cookies de la vitrine, propagée par
   * le cookie cross-sous-domaine). Pilote le NIVEAU de détail des conversions
   * envoyées aux régies, jamais le fait d'en envoyer :
   *   - `true`  → advanced matching complet (e-mail/téléphone hashés, IP/UA, _fbp/_ttp) ;
   *   - absent/`false` → conversion ANONYME : aucune PII, aucun signal navigateur,
   *     seul l'identifiant de clic publicitaire que la régie nous a elle-même transmis
   *     (`fbclid` → `fbc`, `ctwa_clid`, `ttclid`) porte l'attribution. Sans identifiant
   *     de clic il n'y a rien à attribuer : l'event n'est pas envoyé du tout.
   *
   * ⚠️ Seule clé de l'attribution qui n'est PAS first-touch — un consentement donné
   * (ou retiré) plus tard dans la conversation doit s'appliquer aux events suivants.
   */
  marketing_consent: Type.Optional(Type.Boolean()),

  /**
   * Garde « une seule fois » des events funnel déjà émis pour ce lead
   * (clé = TrackingEventKey, valeur = date ISO d'émission).
   */
  events_sent: Type.Optional(Type.Record(Type.String({ maxLength: 40 }), Type.String({ maxLength: 40 }))),

  /**
   * Anti-répétition des ALERTES ops déjà postées pour ce lead (clé
   * `<event>:<motif>`, valeur = date ISO). Volontairement SÉPARÉ d'`events_sent` :
   * un marqueur de confort humain ne doit jamais pouvoir bloquer une émission.
   */
  alerts_sent: Type.Optional(Type.Record(Type.String({ maxLength: 60 }), Type.String({ maxLength: 40 }))),
});

export type LeadAttribution = Static<typeof LeadAttributionSchema>;

/**
 * Attribution marketing d'un PRO, persistée dans `professional.marketing_attribution`
 * (JSONB), capturée UNE fois à l'inscription depuis le payload signup (consentement
 * coché sur la page register + identifiants pub du navigateur).
 *
 * Pourquoi une colonne dédiée : un pro inscrit en direct (sans passer par Pulse) n'a
 * pas de lead, donc pas de `lead.qualification.attribution`. Sans ce snapshot, le
 * consentement donné à l'inscription ne vivait qu'une requête : `signup_completed`
 * partait chez les régies, puis `onboarding_completed` et `became_paid` étaient
 * refusés faute de consentement ET d'identifiant de clic (« ni consentement ni
 * identifiant de clic ») — la conversion payante d'un inscrit direct était perdue.
 *
 * Sous-ensemble strict de `LeadAttributionSchema` (mêmes clés, mêmes bornes) : PAS de
 * PII — l'e-mail/téléphone vivent déjà sur le compte. Quand le pro A un lead, c'est le
 * lead qui reste la source de vérité (cf. `FunnelTrackingService` sujet
 * `{ professional_id }`).
 *
 * Deux familles de champs, deux régimes juridiques distincts :
 *   - les identifiants de clic (`fbclid`, `oppref`, `_fbp`…) sont lus dans des cookies
 *     de régie ⇒ capturés SEULEMENT après consentement marketing ;
 *   - le canal d'acquisition (`utm_*`, `referrer_domain`) est lu dans l'URL et le
 *     referrer, sans aucun accès au terminal ⇒ capturé DANS TOUS LES CAS, et jamais
 *     transmis aux régies (cf. `tracking/attribution-params.ts`). C'est ce qui permet
 *     de répondre à « d'où vient cet inscrit ? » quand la conversion pub est refusée.
 */
export const ProMarketingAttributionSchema = Type.Object({
  /** Décision exprimée sur la page d'inscription (`tracking_consent`). */
  marketing_consent: Type.Boolean(),
  /** Horodatage de la capture (= inscription). */
  captured_at: Type.String({ format: 'date-time' }),
  // Meta
  fbclid: Type.Optional(Type.String({ maxLength: 512 })),
  fbp: Type.Optional(Type.String({ maxLength: 128 })),
  fbc: Type.Optional(Type.String({ maxLength: 256 })),
  ctwa_clid: Type.Optional(Type.String({ maxLength: 512 })),
  // TikTok
  ttclid: Type.Optional(Type.String({ maxLength: 512 })),
  ttp: Type.Optional(Type.String({ maxLength: 128 })),
  // OpenAI Ads
  oppref: Type.Optional(Type.String({ maxLength: 512 })),
  obref: Type.Optional(Type.String({ maxLength: 128 })),
  // Identifiant visiteur première partie (cookie `propelsia_vid`, consentement marketing)
  visitor_id: Type.Optional(Type.String({ maxLength: VISITOR_ID_MAX_LENGTH })),
  // --- Canal d'acquisition (mesure interne, hors consentement, jamais envoyé aux régies) ---
  utm_source: Type.Optional(Type.String({ maxLength: 128 })),
  utm_medium: Type.Optional(Type.String({ maxLength: 128 })),
  utm_campaign: Type.Optional(Type.String({ maxLength: 128 })),
  /** Hôte du site référent quand il est externe (`chatgpt.com`…), jamais le chemin. */
  referrer_domain: Type.Optional(Type.String({ maxLength: REFERRER_DOMAIN_MAX_LENGTH })),
  utm_content: Type.Optional(Type.String({ maxLength: 128 })),
  utm_term: Type.Optional(Type.String({ maxLength: 128 })),
  utm_id: Type.Optional(Type.String({ maxLength: 64 })),
  /** Identifiants Meta de la pub cliquée (`hsa_cam` / `hsa_grp` / `hsa_ad` de l'URL). */
  meta_campaign_id: Type.Optional(Type.String({ maxLength: 32 })),
  meta_adset_id: Type.Optional(Type.String({ maxLength: 32 })),
  meta_ad_id: Type.Optional(Type.String({ maxLength: 32 })),
  /** Chemin de la première page vue sur la vitrine (`/beaute`…), sans query. */
  landing_page: Type.Optional(Type.String({ maxLength: LANDING_PATH_MAX_LENGTH })),
  /** Bras de l'A/B de la LP (lettre : `c` = quiz, ou valeur d'un lien de campagne), recopié depuis `lead.lp_variant`. */
  lp_variant: Type.Optional(Type.String({ maxLength: 64 })),
  /** Verticale de la pub d'arrivée (`/start?v=`), recopiée depuis le quiz (onboarding v4). */
  quiz_vertical: Type.Optional(Type.String({ maxLength: 32 })),
});

export type ProMarketingAttribution = Static<typeof ProMarketingAttributionSchema>;

/**
 * Beacon de la PREVIEW (client → backend, qui fan-out vers les régies + marque
 * `events_sent`). Le vocabulaire accepté est DÉRIVÉ du flag `preview_emittable`
 * de la taxonomie : le endpoint étant public (protégé par le seul token de
 * preview), on borne aux events que la preview a légitimement à émettre.
 */
export const LeadPreviewTrackEventRequestSchema = Type.Object({
  event: Type.Union(PREVIEW_CLIENT_EVENT_KEYS.map((k) => Type.Literal(k))),
  /** URL de la page preview (event_source_url CAPI). */
  url: Type.Optional(Type.String({ maxLength: 1024 })),
  /** Propriétés libres bornées (ex. style_key, placement). */
  props: Type.Optional(
    Type.Record(
      Type.String({ maxLength: 40 }),
      Type.Union([Type.String({ maxLength: 200 }), Type.Number(), Type.Boolean()]),
      { maxProperties: 8 },
    ),
  ),
});

export type LeadPreviewTrackEventRequestObject = Static<typeof LeadPreviewTrackEventRequestSchema>;
